import { useEffect, useState, useCallback } from "react";
import api, { formatApiError, fmtDateTime } from "@/lib/apiClient";
import EmployeeLayout from "@/layouts/EmployeeLayout";
import { logoSrc } from "@/components/BrandLogo";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { SubmissionView, downloadSubmissionPdf } from "@/components/forms/SubmissionView";
import { FileText, Megaphone, Link as LinkIcon, ExternalLink, CheckCircle2, AlertTriangle, Download } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";

const TYPES = [
  { v: "document", label: "Documents", icon: FileText },
  { v: "toolbox", label: "Toolbox", icon: Megaphone },
  { v: "link", label: "Links", icon: LinkIcon },
];
const iconFor = (t) => (TYPES.find((x) => x.v === t) || TYPES[0]).icon;

function SubmittedForms() {
  const [rows, setRows] = useState(null);
  const [viewId, setViewId] = useState(null);
  const [q, setQ] = useState("");
  useEffect(() => { api.get("/shared-submissions").then((r) => setRows(r.data)).catch(() => setRows([])); }, []);
  if (rows === null) return null;
  const shown = rows.filter((s) => `${s.form_title} ${s.employee_name} ${s.site_name || ""}`.toLowerCase().includes(q.toLowerCase()));
  return (
    <div data-testid="submitted-forms">
      <Input data-testid="submitted-forms-search" placeholder="Search by form, person or site…" value={q} onChange={(e) => setQ(e.target.value)} className="mb-3 bg-white" />
      {shown.length === 0 && <div className="text-center text-zinc-400 py-12" data-testid="submitted-forms-empty">No shared submissions yet.</div>}
      <div className="space-y-2">
        {shown.map((s) => (
          <div key={s.id} data-testid={`shared-sub-${s.id}`} className="bg-white border border-zinc-200 rounded-lg p-3 flex items-center gap-3">
            <button type="button" className="flex-1 min-w-0 text-left" onClick={() => setViewId(s.id)} data-testid={`shared-sub-open-${s.id}`}>
              <div className="font-medium text-slate-900 truncate">{s.form_title}</div>
              <div className="text-xs text-zinc-500 truncate">{s.employee_name} · {fmtDateTime(s.submitted_at)}{s.site_name ? ` · ${s.site_name}` : ""}</div>
            </button>
            <Button data-testid={`shared-sub-pdf-${s.id}`} size="sm" variant="outline" className="h-8" onClick={() => downloadSubmissionPdf(s)}><Download className="h-3.5 w-3.5" /></Button>
          </div>
        ))}
      </div>
      <SubmissionView id={viewId} onClose={() => setViewId(null)} />
    </div>
  );
}

export default function EmployeeResources() {
  const [items, setItems] = useState([]);
  const [cats, setCats] = useState([]);
  const [typeFilter, setTypeFilter] = useState("all");
  const [confirmR, setConfirmR] = useState(null);
  const [understood, setUnderstood] = useState(false);

  const load = useCallback(() => {
    api.get("/resources/feed").then((r) => { setItems(r.data.resources || []); setCats(r.data.categories || []); }).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);

  const markRead = async (r, body) => {
    try { await api.post(`/resources/${r.id}/read`, body || {}); setItems((p) => p.map((x) => x.id === r.id ? { ...x, read: true, overdue: false, reconfirm: false } : x)); toast.success(r.must_read ? "Confirmed" : "Marked as read"); setConfirmR(null); window.dispatchEvent(new Event("library-read")); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const askConfirm = (r) => { if (!r.must_read) { markRead(r); return; } setUnderstood(false); setConfirmR(r); };
  const mustPending = items.filter((r) => r.must_read && !r.read);
  const open = (r) => { if (r.type === "link" || (!r.file_url && r.url)) { window.open(r.url, "_blank"); return; } const src = logoSrc(r.file_url); if (src) window.open(src, "_blank"); };

  const shown = items.filter((r) => typeFilter === "all" || r.type === typeFilter);
  const groups = [...cats.map((c) => ({ id: c.id, name: c.name })), { id: "none", name: "Uncategorised" }]
    .map((g) => ({ ...g, items: shown.filter((r) => (r.category_id || "none") === g.id) }))
    .filter((g) => g.items.length > 0);
  const unread = items.filter((r) => !r.read).length;

  return (
    <EmployeeLayout title="Resources">
      <div className="mb-4">
        <h1 className="text-xl font-heading font-bold text-slate-900">Resource Library</h1>
        <p className="text-sm text-zinc-500" data-testid="emp-unread-count">{unread > 0 ? `${unread} unread` : "You're all caught up"}</p>
      </div>
      {mustPending.length > 0 && (
        <div data-testid="must-read-banner" className="mb-4 text-sm bg-rose-50 border border-rose-200 rounded-md px-3 py-2.5 text-rose-900">
          <div className="font-semibold flex items-center gap-1.5"><AlertTriangle className="h-4 w-4" />{mustPending.length} must-read {mustPending.length === 1 ? "document" : "documents"} to confirm</div>
          <p className="text-xs mt-0.5">You can't submit your timesheet until these are confirmed.</p>
        </div>
      )}
      <div className="flex gap-1 mb-4 overflow-x-auto kqm-scroll">
        {[["all", "All"], ...TYPES.map((t) => [t.v, t.label]), ["submitted", "Submitted Forms"]].map(([v, l]) => (
          <button key={v} type="button" data-testid={`emp-type-${v}`} onClick={() => setTypeFilter(v)} className={`text-xs px-3 py-1.5 rounded-full border whitespace-nowrap ${typeFilter === v ? "bg-slate-900 text-white border-slate-900" : "bg-white text-zinc-600 border-zinc-200"}`}>{l}</button>
        ))}
      </div>
      {typeFilter === "submitted" && <SubmittedForms />}
      {typeFilter !== "submitted" && groups.length === 0 && <div className="text-center text-zinc-400 py-12" data-testid="emp-resources-empty">Nothing shared with you yet.</div>}
      <div className="space-y-6">
        {typeFilter !== "submitted" && groups.map((g) => (
          <div key={g.id} data-testid={`emp-cat-${g.id}`}>
            <div className="text-xs font-bold uppercase tracking-wider text-zinc-500 mb-2">{g.name}</div>
            <div className="space-y-2">
              {g.items.map((r) => { const Icon = iconFor(r.type); return (
                <div key={r.id} data-testid={`emp-resource-${r.id}`} className="bg-white border border-zinc-200 rounded-lg p-3">
                  <div className="flex items-start gap-3">
                    <div className="h-9 w-9 rounded-md bg-zinc-100 flex items-center justify-center shrink-0"><Icon className="h-4 w-4 text-slate-700" /></div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap"><span className="font-medium text-slate-900">{r.title}</span>{!r.read && <span data-testid={`emp-unread-${r.id}`} className="h-2 w-2 rounded-full bg-amber-500 shrink-0" title="Unread" />}
                        {r.must_read && <span data-testid={`emp-mustread-${r.id}`} className="text-[10px] font-bold uppercase px-1.5 py-0.5 rounded bg-rose-100 text-rose-700">Must read</span>}</div>
                      {r.must_read && !r.read && (r.due_date || r.reconfirm) && <p data-testid={`emp-due-${r.id}`} className={`text-xs mt-0.5 ${r.overdue ? "text-rose-600 font-semibold" : "text-zinc-500"}`}>{r.reconfirm ? "Updated, please confirm again. " : ""}{r.due_date ? `${r.overdue ? "Overdue, was due" : "Due"} ${r.due_date.split("-").reverse().join("/")}` : ""}</p>}
                      {r.description && <p className="text-xs text-zinc-500 mt-0.5">{r.description}</p>}
                      <div className="flex items-center gap-2 mt-2">
                        <Button data-testid={`emp-open-${r.id}`} size="sm" variant="outline" className="h-8" onClick={() => open(r)}><ExternalLink className="h-3.5 w-3.5 mr-1" />Open</Button>
                        {r.read ? <span className="text-xs text-emerald-600 font-semibold inline-flex items-center gap-1"><CheckCircle2 className="h-3.5 w-3.5" />{r.must_read ? "Confirmed" : "Read"}</span>
                          : <Button data-testid={`emp-read-${r.id}`} size="sm" className="h-8 bg-amber-500 text-black font-bold hover:bg-amber-600" onClick={() => askConfirm(r)}>{r.must_read ? "Confirm read" : "I've read this"}</Button>}
                      </div>
                    </div>
                  </div>
                </div>
              ); })}
            </div>
          </div>
        ))}
      </div>
      <Dialog open={!!confirmR} onOpenChange={(o) => !o && setConfirmR(null)}>
        <DialogContent className="max-w-sm" data-testid="must-read-dialog">
          <DialogHeader><DialogTitle>{confirmR?.title}</DialogTitle><DialogDescription>Open and read the document before confirming.</DialogDescription></DialogHeader>
          <Button data-testid="must-read-open" variant="outline" onClick={() => open(confirmR)}><ExternalLink className="h-4 w-4 mr-1" />Open document</Button>
          <label className="flex items-start gap-2 text-sm cursor-pointer"><Checkbox data-testid="must-read-understood" checked={understood} onCheckedChange={(v) => setUnderstood(!!v)} className="mt-0.5" />I have read and understood this document</label>
          <Button data-testid="must-read-confirm" disabled={!understood} onClick={() => markRead(confirmR, { understood: true })} className="bg-amber-500 text-black font-bold hover:bg-amber-600">Confirm</Button>
        </DialogContent>
      </Dialog>
    </EmployeeLayout>
  );
}
